import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';
import { FEATURE_SCREEN_SRC } from './featureScreens';

const CHIPS = [
  {
    key: 'strength',
    tagColor: LANDING_COLOR.mintDeep,
    tagBg: '#E6FCF5',
    position: { top: '12%', left: -70 },
  },
  { key: 'improve', tagColor: '#E03131', tagBg: '#FFF5F5', position: { top: '40%', right: -70 } },
  { key: 'next', tagColor: '#3B5BDB', tagBg: '#EDF2FF', position: { bottom: '4%', left: -40 } },
] as const;

/** STEP 4 — 종합 리포트. 기울인 리포트 화면 + 떠 있는 평가 칩 */
export const ReportPreview = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-report-title">
      <Box css={styles.inner}>
        <SectionHead
          id="ai-report-title"
          step={4}
          titleKey="ai_interviews.landing.report.title"
          descriptionKey="ai_interviews.landing.report.desc"
        />

        <Box css={styles.stage}>
          <Box css={styles.browser}>
            <Box css={styles.browserBar} aria-hidden>
              <i />
              <i />
              <i />
            </Box>
            {/* eslint-disable-next-line @next/next/no-img-element -- 리포트 캡처, 어드민 CDN */}
            <img
              src={FEATURE_SCREEN_SRC.report}
              alt={t('ai_interviews.landing.report.image_alt')}
              width={1024}
              height={630}
              css={styles.screen}
            />
          </Box>
          {CHIPS.map((chip) => (
            <Box key={chip.key} css={styles.chip} style={chip.position}>
              <Text
                component="span"
                css={styles.chipTag}
                style={{ color: chip.tagColor, background: chip.tagBg }}
              >
                {t(`ai_interviews.landing.report.${chip.key}.tag`)}
              </Text>
              <Text css={styles.chipText}>
                {t(`ai_interviews.landing.report.${chip.key}.text`)}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    overflow: 'hidden',
    background: '#fff',
    padding: `${LANDING_SPACE.sectionY} ${LANDING_SPACE.pageX}`,
  }),
  inner: css({
    maxWidth: 1200,
    margin: '0 auto',
  }),
  stage: css({
    position: 'relative',
    maxWidth: 1000,
    margin: `${LANDING_SPACE.blockTop} auto 0`,
    perspective: 1600,
    [LANDING_MOBILE_MQ]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
  }),
  browser: css({
    overflow: 'hidden',
    borderRadius: 14,
    background: '#fff',
    boxShadow: '0 30px 70px -36px rgba(17,19,21,.45)',
    transform: 'rotateX(8deg) rotateZ(-2deg)',
    [LANDING_MOBILE_MQ]: {
      marginBottom: 14,
      transform: 'none',
      boxShadow: '0 16px 40px -24px rgba(17,19,21,.45)',
    },
  }),
  browserBar: css({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: 28,
    padding: '0 12px',
    background: '#EEF0F2',
    '& > i': {
      width: 9,
      height: 9,
      borderRadius: '50%',
      background: '#D0D5DB',
    },
  }),
  screen: css({
    display: 'block',
    width: '100%',
    height: 'auto',
  }),
  chip: css({
    position: 'absolute',
    maxWidth: 260,
    padding: '12px 16px',
    borderRadius: 16,
    background: '#fff',
    boxShadow: '0 16px 36px -14px rgba(17,19,21,.35)',
    [LANDING_MOBILE_MQ]: {
      position: 'static',
      maxWidth: 'none',
      boxShadow: 'none',
      border: `1px solid ${LANDING_COLOR.line}`,
    },
  }),
  chipTag: css({
    display: 'inline-block',
    marginBottom: 6,
    padding: '2px 9px',
    borderRadius: 999,
    fontSize: LANDING_FONT.micro,
    fontWeight: 800,
  }),
  chipText: css({
    color: LANDING_COLOR.ink,
    fontSize: LANDING_FONT.body,
    fontWeight: 600,
    lineHeight: 1.45,
    wordBreak: 'keep-all',
  }),
};
